import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { PageHero, SectionLabel, CtaSection } from "../components/common";
import { SOLUTIONS, SOLUTION_DETAILS, PRODUCTS } from "../data/site";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const get = (slug) => ({ base: SOLUTIONS.find((s) => s.slug === slug), detail: SOLUTION_DETAILS[slug] });

const Row = ({ label, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] border-t border-slate-200">
    <div className="hidden md:flex items-start p-6 md:p-7 text-xs uppercase tracking-widest text-slate-500 font-semibold bg-brand-50/40">{label}</div>
    {children}
  </div>
);

const Cell = ({ label, children, className = "" }) => (
  <div className={`p-6 md:p-7 md:border-l border-slate-200 ${className}`}>
    <div className="md:hidden text-xs uppercase tracking-widest text-brand-700 font-semibold mb-3">{label}</div>
    {children}
  </div>
);

function Selector({ value, onChange, exclude, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-none border-slate-300 h-12 focus:ring-brand-800 font-heading font-semibold text-brand-950">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SOLUTIONS.map((s) => (
          <SelectItem key={s.slug} value={s.slug} disabled={s.slug === exclude} data-testid={`compare-option-${s.slug}`}>
            {s.title}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function CompareSolutions() {
  const [aSlug, setA] = useState(SOLUTIONS[0].slug);
  const [bSlug, setB] = useState(SOLUTIONS[1].slug);

  const a = get(aSlug);
  const b = get(bSlug);
  const cols = [a, b];

  const RelatedList = ({ detail }) => (
    <div className="flex flex-wrap gap-2">
      {(detail.relatedProducts || []).map((ps) => {
        const p = PRODUCTS.find((x) => x.slug === ps);
        if (!p) return null;
        return (
          <Link key={ps} to={`/products/${ps}`} data-testid={`compare-related-${ps}`} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 text-xs text-slate-700 hover:border-brand-800 hover:text-brand-800 transition-colors">
            {p.name.replace("STERILTRON ", "")} <ArrowUpRight size={12} />
          </Link>
        );
      })}
    </div>
  );

  return (
    <>
      <Seo
        title="Compare Solutions | STERILTRON"
        description="Compare STERILTRON solutions side by side — overview, capabilities, process and specifications — to find the right fit for your facility."
        path="/solutions/compare"
      />
      <PageHero
        label="Compare"
        titleLines={["Compare Solutions", "Side by Side"]}
        description="Choose any two STERILTRON solutions to compare their capabilities, process and specifications and identify the right fit for your facility."
      />

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Selectors + sticky header */}
          <div className="sticky top-16 md:top-20 z-30 bg-white/90 backdrop-blur-md border border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr]">
              <div className="hidden md:flex items-center p-5 text-xs uppercase tracking-widest text-slate-400 font-semibold">Solution</div>
              {[{ v: aSlug, set: setA, ex: bSlug, t: "compare-select-a" }, { v: bSlug, set: setB, ex: aSlug, t: "compare-select-b" }].map((c, i) => (
                <div key={i} className="p-4 md:p-5 md:border-l border-slate-200 border-t md:border-t-0">
                  <Selector value={c.v} onChange={c.set} exclude={c.ex} testid={c.t} />
                </div>
              ))}
            </div>
          </div>

          {/* Comparison body */}
          <div className="mt-8 border border-slate-200 bg-white">
            {/* Image + tagline */}
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr]">
              <div className="hidden md:block bg-brand-50/40" />
              {cols.map((c, i) => (
                <div key={i} className="md:border-l border-slate-200 border-t md:border-t-0">
                  <div className="overflow-hidden bg-slate-900">
                    <img src={c.base.image} alt={c.base.title} className="w-full h-48 md:h-56 object-cover" />
                  </div>
                  <div className="p-6 md:p-7">
                    <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-brand-950">{c.base.title}</h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.detail.tagline}</p>
                    <Link to={`/solutions/${c.base.slug}`} data-testid={`compare-view-${c.base.slug}`} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 hover:gap-2.5 transition-all">
                      Full Solution <ArrowUpRight size={13} />
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <Row label="Overview">
              {cols.map((c, i) => (
                <Cell key={i} label="Overview"><p className="text-sm text-slate-700 leading-relaxed">{c.detail.overview}</p></Cell>
              ))}
            </Row>

            <Row label="Key Capabilities">
              {cols.map((c, i) => (
                <Cell key={i} label="Key Capabilities">
                  <ul className="space-y-2.5">
                    {c.base.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <span className="mt-0.5 flex-shrink-0 w-4 h-4 bg-brand-800 text-white flex items-center justify-center"><Check size={11} /></span>
                        <span className="leading-relaxed">{f}</span>
                      </li>
                    ))}
                  </ul>
                </Cell>
              ))}
            </Row>

            <Row label="Process">
              {cols.map((c, i) => (
                <Cell key={i} label="Process">
                  <ol className="space-y-3">
                    {c.detail.process.map((p, j) => (
                      <li key={p.title} className="flex gap-3 text-sm">
                        <span className="font-mono text-xs text-slate-400 pt-0.5">{String(j + 1).padStart(2, "0")}</span>
                        <span><span className="font-semibold text-brand-950">{p.title}.</span> <span className="text-slate-600">{p.text}</span></span>
                      </li>
                    ))}
                  </ol>
                </Cell>
              ))}
            </Row>

            <Row label="Specifications">
              {cols.map((c, i) => (
                <Cell key={i} label="Specifications">
                  <dl className="divide-y divide-slate-100">
                    {c.detail.specs.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-4 py-2.5">
                        <dt className="text-sm text-slate-500">{k}</dt>
                        <dd className="text-sm font-mono text-brand-900 text-right">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </Cell>
              ))}
            </Row>

            <Row label="Related Products">
              {cols.map((c, i) => (
                <Cell key={i} label="Related Products"><RelatedList detail={c.detail} /></Cell>
              ))}
            </Row>

            <Row label="">
              {cols.map((c, i) => (
                <Cell key={i} label="">
                  <Link to="/contact" data-testid={`compare-cta-${c.base.slug}`} className="group inline-flex items-center gap-2 bg-brand-800 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-brand-900 transition-colors">
                    Discuss This Solution <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Cell>
              ))}
            </Row>
          </div>

          <div className="mt-8">
            <Link to="/solutions" data-testid="compare-all-solutions" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-brand-800 hover:gap-3 transition-all">
              <SectionLabel>Back to all solutions</SectionLabel>
            </Link>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
